import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, ImageOff } from 'lucide-react';
import { getBeforeAfter } from '../lib/api';

const BeforeAfterSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setItems(await getBeforeAfter());
      } catch (e) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <section className="bg-[#0A1628] py-24 md:py-32 relative overflow-hidden">
      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="max-w-7xl mx-auto px-6 relative z-10"
      >
        {/* Section Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <span className="text-[#60A5FA] text-lg font-mono mb-4 block">Resultados reales</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Antes y <span className="text-[#60A5FA]">Después</span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Mira las transformaciones que logramos en cada vehículo
          </p>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="aspect-[16/10] bg-[#0F1D33]/50 rounded-3xl animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <motion.div
            variants={itemVariants}
            className="bg-gradient-to-r from-[#60A5FA]/10 to-[#3B82F6]/10 border border-[#60A5FA]/30 rounded-3xl p-12 text-center backdrop-blur-sm"
          >
            <div className="w-20 h-20 bg-[#60A5FA]/20 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <ImageOff className="w-10 h-10 text-[#60A5FA]" />
            </div>
            <h3 className="text-white font-semibold text-2xl mb-3">
              Próximamente: Transformaciones reales
            </h3>
            <p className="text-white/60 max-w-xl mx-auto">
              Pronto compartiremos comparaciones de antes y después de nuestros trabajos.
            </p>
          </motion.div>
        ) : (
          // Dynamic grid - grows automatically
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((item) => (
              <motion.div
                key={item.id}
                variants={itemVariants}
                whileHover={{ y: -8 }}
                className="group relative bg-[#0F1D33]/50 backdrop-blur-lg border border-white/10 rounded-3xl overflow-hidden hover:border-[#60A5FA]/50 transition-all duration-300"
              >
                {/* Before/After Images */}
                <div className="relative aspect-[16/10] grid grid-cols-2">
                  <div className="relative overflow-hidden">
                    <img
                      src={item.before_image}
                      alt="Antes"
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute bottom-3 left-3 px-3 py-1 rounded-full text-xs font-semibold bg-black/60 text-white/90 uppercase tracking-wider">
                      Antes
                    </span>
                  </div>
                  <div className="relative overflow-hidden">
                    <img
                      src={item.after_image}
                      alt="Después"
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute bottom-3 right-3 px-3 py-1 rounded-full text-xs font-semibold bg-[#60A5FA]/80 text-white uppercase tracking-wider">
                      Después
                    </span>
                  </div>

                  {/* Arrow Divider */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                    <div className="w-12 h-12 bg-[#60A5FA] rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <ArrowRight className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-white mb-2">{item.service_title}</h3>
                  {item.description && <p className="text-white/60 text-sm">{item.description}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </section>
  );
};

export default BeforeAfterSection;
